import {
  AccountRole,
  AdgroupConfig,
  AdgroupFormData,
  BrandSafetyType,
  CampaignFormData,
  REGION_ID,
  ExtractRequestFn,
  CreateMode,
} from '@tt4b/ttam-enums';
import {
  ValidationError,
  useAdgroupLocation,
  useDSAAssignInfoApi,
  useRequest,
  useStdAdgroupIs,
  useStore,
  NetStateEnum,
  StateEnum,
  useAdgroupMode,
  useStdAccountStore,
} from '@tt4b/ttam-hooks';

import { transformLocations } from '@tt4b/ttam-utils';
import { computed, Ref, ref } from '@vue/composition-api';
import { i18n } from './../../../i18n';

export function usePropsSupportBrandSafety(opt: { adgroupData: Ref<AdgroupFormData> }) {
  const isSupportBrandSafety = computed(() =>
    [BrandSafetyType.FLAGSHIP, BrandSafetyType.SELCTED_INVENTORY].includes(opt.adgroupData.value.brandSafety),
  );
  return {
    isSupportBrandSafety,
  };
}

export function useTreeProps() {
  const treeProps = ref({
    label: 'translation',
    filterLabel: 'filterLabel',
  });
  return {
    treeProps,
  };
}

export function usePropsDisabledLocation(opt: {
  adgroupData: Ref<AdgroupFormData>;
  adgroupConfig: Ref<AdgroupConfig>;
}) {
  const disabledLocation = computed(
    () => opt.adgroupConfig.value.disableLocation && opt.adgroupData.value?.brandSafety !== BrandSafetyType.UN_SAFE,
  );
  return {
    disabledLocation,
  };
}

export function useRules(opt: { adgroupData: Ref<AdgroupFormData> }) {
  const { state } = useStore();
  const areaData = state[StateEnum.areaData].data;
  const areaMap = computed(() => areaData?.value?.areaMap ?? {});
  return {
    locationValidator: computed(() => [
      {
        validator: (rule, value: number[], callback, data: AdgroupFormData) => {
          const locationParams = transformLocations({
            particleLocations: value,
            locationParams: {
              areaMap: areaMap.value,
              isSplitTestBidding: false,
              isSplitTestCreative: false,
            },
          });
          const country = (locationParams.country as number[]) ?? [];
          if (!value.length) {
            return callback(
              new ValidationError({
                message: i18n.t('adgroup_district_hint'),
                messageI18nKey: 'adgroup_district_hint',
                moduleName: i18n.t('adgroup_target_card'),
                subModuleName: i18n.t('adgroup_district'),
              }),
            );
          }
          // 品牌安全支持一个国家
          if (
            [BrandSafetyType.FLAGSHIP, BrandSafetyType.SELCTED_INVENTORY].includes(opt.adgroupData.value.brandSafety)
          ) {
            if (country?.length > 1) {
              return callback(
                new ValidationError({
                  message: i18n.t('module_location_single_country_error'),
                  messageI18nKey: 'module_location_single_country_error',
                  moduleName: i18n.t('adgroup_target_card'),
                  subModuleName: i18n.t('adgroup_district'),
                }),
              );
            }
          }

          // 校验国家和 CA 选择的商品库是否匹配
          // const { validateLocationForCA } = useValidatorCollectionAds();
          // if (!validateLocationForCA(rule, country, callback)) {
          //   return;
          // }
          return callback();
        },
        trigger: 'custom',
      },
    ]),
  };
}

export function useDSA() {
  const { data: DSAInfo } = useRequest(useDSAAssignInfoApi);
  const DSAHref = computed(() => `/cg/contract_creation/contract/dsa/?register_code=${DSAInfo?.value?.token}`);
  const urlSignLink = computed(() => `/cg/contract_creation/contract/dsa/?register_code=${DSAInfo?.value?.token}`);
  const DSALoading = ref(false);

  const openDSAClick = () => {
    DSALoading.value = true;
    try {
      // DOM
      const a = document.createElement('a');
      a.href = urlSignLink.value;
      a.setAttribute('target', 'blank');
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } finally {
      DSALoading.value = false;
    }
  };

  return {
    DSAHref,
    DSAInfo,
    DSALoading,
    openDSAClick,
  };
}

export function usePropsJapanVideoTipsVisible(opt: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
}) {
  const { state } = useStore();
  const areaData = state[StateEnum.areaData].data;
  const areaMap = computed(() => areaData?.value?.areaMap ?? {});
  const { isHasTikTok } = useStdAdgroupIs({
    adgroupFormData: opt.adgroupData,
    campaignFormData: opt.campaignData,
  });
  const findCountryById = (id: number) => {
    const curArea = areaMap.value[id];
    if (!curArea?.parentId) {
      return curArea;
    }
    return findCountryById(curArea.parentId);
  };
  const japanVideoTipsVisible = computed(() => {
    const isHasJapan = opt.adgroupData.value.particleLocations?.some(id => findCountryById(id)?.id === REGION_ID.JAPAN);
    return isHasTikTok.value && isHasJapan;
  });
  return {
    japanVideoTipsVisible,
  };
}

export function usePropsDSATipsVisible(opt: {
  adgroupData: Ref<AdgroupFormData>;
  DSAInfo: Ref<ExtractRequestFn<typeof useDSAAssignInfoApi>>;
}) {
  const { state } = useStore();
  const { isSelfAccount } = useStdAccountStore();
  const areaData = state[StateEnum.areaData].data;
  const areaMap = computed(() => areaData?.value?.areaMap ?? {});
  const { adgroupData, DSAInfo } = opt;
  const DSAIds = [REGION_ID.FRANCE, REGION_ID.ITALY, REGION_ID.UNITED_KINGDOM, REGION_ID.SPAIN, REGION_ID.POLAND];
  const countryIds = computed(() =>
    adgroupData.value.particleLocations.filter(item => areaMap.value[item]?.level === 1),
  );

  const dsaTipsVisible = computed(
    () =>
      isSelfAccount.value &&
      DSAInfo.value?.permitted_to_sign &&
      !DSAInfo.value?.signed &&
      countryIds.value.some(regionId => DSAIds.includes(regionId)),
  );
  return {
    dsaTipsVisible,
  };
}

export function useDataSource(opt: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
  filterMethod;
}) {
  const { adgroupData, campaignData, filterMethod } = opt;
  const { objectiveType } = campaignData.value;
  const { inventoryFlow, platform, brandSafety, brandSafetyPartner, classify } = adgroupData.value;
  const { getStore } = useStore();

  if (!inventoryFlow?.length) {
    return {};
  }

  const areaParam = {
    classify,
    platform,
    objectiveType,
    inventoryFlow,
    brandSafety: brandSafety === BrandSafetyType.FLAGSHIP ? brandSafetyPartner && brandSafety : brandSafety,
    brandSafetyPartner,
    currentLang: 'en',
  };
  const { data, isLoading, run: loadData } = getStore(NetStateEnum.areaData, areaParam);

  const areaList = computed(() => data.value?.areaListForLocationTree ?? []);
  const metroIdsMap = computed(() => data.value?.metroIdsMap);
  const regionIdsMap = computed(() => data.value?.regionIdsMap);
  const virtualNodeIdArr = computed(() => data.value?.virtualNodeIdArr);
  const virtualNodeIdParentIdMap = computed(() => data.value?.virtualNodeIdParentIdMap);
  // 在电商场景下会过滤出商品库对应的国家
  const availableAreaList = computed(() =>
    typeof filterMethod.value === 'function' ? filterMethod.value(areaList.value) : areaList.value,
  );

  return {
    data,
    loadData,
    isLoading,
    areaList,
    availableAreaList,
    virtualNodeIdArr,
    virtualNodeIdParentIdMap,
    metroIdsMap,
    regionIdsMap,
  };
}

export function usePropsOfflineAreaTipsVisible(opt: {
  adgroupData: Ref<AdgroupFormData>;
  createMode: Ref<CreateMode>;
}) {
  const { adgroupData, createMode } = opt;
  const { isEdit } = useAdgroupMode({
    createMode,
  });
  const offlineAreaTipsVisible = ref(false);
  const editParticleLocations = ref([]);
  if (isEdit.value) {
    const { state } = useStore();
    const areaData = state[StateEnum.areaData].data;
    const areaMap = computed(() => areaData?.value?.areaMap ?? {});
    const unsupportedCountriesIds = computed(() => areaData?.value?.unsupportedCountryIds ?? []);
    const { filterBlockedId } = useAdgroupLocation({
      adgroupFormData: adgroupData,
      areaMap,
      areaList: ref([]),
      accountCountry: ref(''),
      unsupportedCountriesIds,
    });
    const preLocationLength = computed(() => adgroupData.value.particleLocations.length);
    editParticleLocations.value = filterBlockedId(adgroupData.value.particleLocations);
    offlineAreaTipsVisible.value = preLocationLength.value !== editParticleLocations.value.length;
  }
  return {
    offlineAreaTipsVisible,
    editParticleLocations,
  };
}

export function usePropsLocationName(options: { adgroupData: Ref<AdgroupFormData> }) {
  const { adgroupData } = options;
  const { state } = useStore();
  const areaData = state[StateEnum.areaData].data;
  const areaMap = computed(() => areaData?.value?.areaMap ?? {});
  const particleLocations = computed(() => adgroupData.value.particleLocations);
  const particleLocationsName = computed(() => particleLocations.value?.map(id => areaMap.value?.[id]?.translation));

  return {
    particleLocationsName,
  };
}

export function useStdLocation(options: { adgroupData: Ref<AdgroupFormData> }) {
  const { adgroupData } = options;
  const { state } = useStore();
  const areaData = computed(() => state[StateEnum.areaData].data.value);
  const areaMap = computed(() => areaData?.value?.areaMap ?? {});
  const areaList = computed(() => state[StateEnum.areaData].data.value?.areaList);
  const accountInfo = computed(() => state[StateEnum.accountInfo].data.value);
  const accountCountry = computed(() => accountInfo.value.account.country);
  const transformResult = computed(() =>
    transformLocations({
      particleLocations: adgroupData.value.particleLocations,
      locationParams: {
        areaMap: areaMap.value,
        isSplitTestBidding: false,
        isSplitTestCreative: false,
      },
    }),
  );
  /** 实际选中的国家 */
  const countryIds = computed(() =>
    adgroupData.value.particleLocations.filter(item => areaMap.value?.[item]?.level === 1),
  );

  /** 全量的国家范围（包含选了city未选country的国家） */
  const fullCountryIds = computed(() => transformResult.value.country ?? []);
  const countryShortName = computed(() =>
    fullCountryIds.value.map(id => areaMap.value[id ?? '']?.code || 0).filter(item => item),
  );

  const provinceIds = computed(() =>
    adgroupData.value.particleLocations.filter(item => areaMap.value[item]?.level === 2),
  );

  const registerCountryId = computed(() => {
    const countryItem = areaList.value?.find(item => item.code === accountCountry.value);
    return countryItem?.id;
  });

  const cityIds = computed(() => adgroupData.value.particleLocations.filter(item => areaMap.value[item]?.level === 3));

  const getLocationNameFromId = ids => ids.map(id => areaMap.value[id]?.translation);
  const getLocationNameFromShortName = names =>
    names.map(name => {
      const id = Object.keys(areaMap.value).filter(v => areaMap.value[v].code === name)[0];
      return areaMap.value[id]?.translation;
    });

  return {
    countryIds,
    fullCountryIds,
    countryShortName,
    provinceIds,
    registerCountryId,
    cityIds,
    getLocationNameFromId,
    getLocationNameFromShortName,
  };
}
